import { useState, useEffect, useMemo, useCallback } from "react";
import { Menu, X, LogOut, ChevronDown, AtSign, User } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useSession } from "@/hooks/useSession";
import { AppModal } from "../modals/AppModal";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  SIDEBAR_NEW_CHAT,
  type SidebarNavItem,
  type SidebarNavLeafItem,
  type SidebarNavSection,
} from "@/config/sidebarNav";
import type { ChatEntry } from "@/types/chat";
import { navigateToChatEntryForApp } from "@/lib/chatNavigation";
import { SYSTEM_ADMIN_NAV_ITEM } from "@/config/sidebarNav";
import { useSystemAdmin } from "@/hooks/useSystemAdmin";
import { deriveWorkspacePersona, getSidebarSectionsForPersona } from "@/config/workspacePersona";

const SIDEBAR_EXPANDED_KEY = "heybodhi:sidebar-expanded";

type ExpandedState = Record<string, boolean>;

const isChatItemActive = (pathname: string, state: unknown, entry: ChatEntry) => {
  const e = (state as { entry?: ChatEntry } | null)?.entry;
  if (entry === "ai_mentor") {
    return pathname === "/" && (e === undefined || e === null || e === "ai_mentor");
  }
  return pathname === "/mentor" && e === entry;
};

const isLinkItemActive = (pathname: string, item: Extract<SidebarNavLeafItem, { kind: "link" }>) => {
  if (item.matchPrefix) return pathname === item.to || pathname.startsWith(`${item.to}/`);
  if (item.to === "/restoration-room") return pathname === item.to || pathname.startsWith(`${item.to}/`);
  return pathname === item.to;
};

const isLeafActive = (pathname: string, state: unknown, item: SidebarNavLeafItem): boolean => {
  if (item.kind === "chat") return isChatItemActive(pathname, state, item.entry);
  return isLinkItemActive(pathname, item);
};

const sectionContainsActive = (pathname: string, state: unknown, section: SidebarNavSection): boolean => {
  for (const item of section.items) {
    if (item.kind === "group") {
      if (item.children.some((child) => isLeafActive(pathname, state, child))) return true;
    } else if (isLeafActive(pathname, state, item)) {
      return true;
    }
  }
  return false;
};

const groupContainsActive = (pathname: string, state: unknown, item: Extract<SidebarNavItem, { kind: "group" }>): boolean =>
  item.children.some((child) => isLeafActive(pathname, state, child));

const readExpandedState = (): ExpandedState => {
  try {
    const raw = localStorage.getItem(SIDEBAR_EXPANDED_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return typeof parsed === "object" && parsed !== null ? (parsed as ExpandedState) : {};
  } catch {
    return {};
  }
};

const writeExpandedState = (state: ExpandedState) => {
  try {
    localStorage.setItem(SIDEBAR_EXPANDED_KEY, JSON.stringify(state));
  } catch {
    // ignore quota / private mode
  }
};

export const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLogout, setIsLogout] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isStartingNewChat, setIsStartingNewChat] = useState(false);
  const [expandedState, setExpandedState] = useState<ExpandedState>(() => readExpandedState());
  const { signOut, userData } = useAuth();
  const { createNewSession } = useSession();
  const { isSystemAdmin } = useSystemAdmin();

  const navSections = useMemo(() => {
    const sections = getSidebarSectionsForPersona(deriveWorkspacePersona(userData));
    if (!isSystemAdmin) return sections;
    return [
      ...sections,
      {
        id: "platform",
        label: "Platform",
        defaultExpanded: true,
        items: [SYSTEM_ADMIN_NAV_ITEM],
      },
    ];
  }, [userData, isSystemAdmin]);

  const isExpanded = useCallback(
    (key: string, defaultExpanded: boolean | undefined) => {
      if (key in expandedState) return expandedState[key];
      return defaultExpanded ?? true;
    },
    [expandedState]
  );

  const setExpanded = useCallback((key: string, open: boolean) => {
    setExpandedState((prev) => {
      const next = { ...prev, [key]: open };
      writeExpandedState(next);
      return next;
    });
  }, []);

  useEffect(() => {
    setExpandedState((prev) => {
      let changed = false;
      const next = { ...prev };

      for (const section of navSections) {
        const sectionKey = `section:${section.id}`;
        if (sectionContainsActive(location.pathname, location.state, section)) {
          if (next[sectionKey] !== true) {
            next[sectionKey] = true;
            changed = true;
          }
        }

        for (const item of section.items) {
          if (item.kind === "group") {
            const groupKey = `group:${item.id}`;
            if (groupContainsActive(location.pathname, location.state, item)) {
              if (next[groupKey] !== true) {
                next[groupKey] = true;
                changed = true;
              }
            }
          }
        }
      }

      if (changed) writeExpandedState(next);
      return changed ? next : prev;
    });
  }, [location.pathname, location.state, navSections]);

  const closeMobile = () => setMobileMenuOpen(false);

  const goToChatEntry = (entry: ChatEntry) => {
    closeMobile();
    navigateToChatEntryForApp(navigate, entry, false, deriveWorkspacePersona(userData));
  };

  /** Close the current ai_mentor session (so it appears in History) and open a fresh hub thread. */
  const goToNewChat = async () => {
    closeMobile();
    setIsStartingNewChat(true);
    try {
      const session = await createNewSession("ai_mentor");
      if (!session) return;
      navigate("/", { replace: true, state: { entry: "ai_mentor" } });
    } finally {
      setIsStartingNewChat(false);
    }
  };

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 768) setMobileMenuOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const renderLeaf = (item: SidebarNavLeafItem) => {
    const Icon = item.icon;
    const isActive = isLeafActive(location.pathname, location.state, item);
    const itemClass = cn(
      "flex items-center px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-200 group relative w-full text-left",
      isActive ? "text-white" : "text-white/70 hover:text-white hover:bg-white/5"
    );

    if (item.kind === "chat") {
      return (
        <li key={item.id}>
          <button type="button" onClick={() => goToChatEntry(item.entry)} className={itemClass}>
            {isActive && <div className="absolute left-0 top-0 h-full w-1 bg-bodhi-orange rounded-r-full" />}
            <Icon className="w-4 h-4 mr-2.5 shrink-0 opacity-90" />
            <span className="leading-snug">{item.label}</span>
          </button>
        </li>
      );
    }

    return (
      <li key={item.id}>
        <Link to={item.to} onClick={closeMobile} className={itemClass}>
          {isActive && <div className="absolute left-0 top-0 h-full w-1 bg-bodhi-orange rounded-r-full" />}
          <Icon className="w-4 h-4 mr-2.5 shrink-0 opacity-90" />
          <span className="leading-snug">{item.label}</span>
        </Link>
      </li>
    );
  };

  const renderNavItem = (item: SidebarNavItem, nested = false) => {
    if (item.kind !== "group") {
      return renderLeaf(item);
    }

    const GroupIcon = item.icon;
    const groupKey = `group:${item.id}`;
    const open = isExpanded(groupKey, item.defaultExpanded);
    const groupActive = groupContainsActive(location.pathname, location.state, item);

    return (
      <li key={item.id}>
        <Collapsible open={open} onOpenChange={(next) => setExpanded(groupKey, next)}>
          <CollapsibleTrigger
            className={cn(
              "flex items-center w-full px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-200 group relative",
              groupActive ? "text-white" : "text-white/70 hover:text-white hover:bg-white/5"
            )}
          >
            {groupActive && <div className="absolute left-0 top-0 h-full w-1 bg-bodhi-orange rounded-r-full" />}
            <GroupIcon className="w-4 h-4 mr-2.5 shrink-0 opacity-90" />
            <span className="leading-snug flex-1 text-left">{item.label}</span>
            <ChevronDown
              size={14}
              strokeWidth={3}
              className={cn("text-white/60 transition-transform duration-200 shrink-0", open && "rotate-180")}
            />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <ul className={cn("mt-0.5 space-y-0.5 border-l border-white/15 ml-4 pl-1", nested && "ml-3")}>
              {item.children.map((child) => renderLeaf(child))}
            </ul>
          </CollapsibleContent>
        </Collapsible>
      </li>
    );
  };

  const NewChatIcon = SIDEBAR_NEW_CHAT.icon;

  return (
    <>
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className={cn(
          "md:hidden fixed top-3 left-3 z-50 p-2 bg-bodhi-teal shadow-lg rounded-lg text-white hover:bg-bodhi-teal/90 transition-colors",
          mobileMenuOpen ? "hidden" : "block"
        )}
      >
        <Menu className="w-5 h-5" />
      </button>

      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 bg-black/60 z-40" onClick={() => setMobileMenuOpen(false)} />
      )}

      <div
        className={cn(
          "w-64 h-screen fixed left-0 top-0 z-50 flex flex-col font-inter transition-transform duration-300 shadow-xl",
          "bg-gradient-to-b from-[#126c74] via-[#a89a5c] to-[#126c74] md:bg-transparent",
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        )}
      >
        <div className="flex flex-col h-full min-h-0">
          <div className="p-6 bg-black/10 backdrop-blur-sm md:bg-transparent shrink-0">
            <button
              type="button"
              disabled={isStartingNewChat}
              onClick={() => void goToNewChat()}
              className="block w-full hover:opacity-80 transition-opacity disabled:cursor-not-allowed disabled:opacity-60"
              aria-label="New chat"
            >
              <img
                src="/HeyBodhiLogo-White.png"
                alt="hey bodhi logo"
                className="h-12 w-auto mx-auto cursor-pointer"
                draggable={false}
              />
            </button>
          </div>

          <div className="px-4 pb-1.5 shrink-0">
            <button
              type="button"
              disabled={isStartingNewChat}
              onClick={() => void goToNewChat()}
              className={cn(
                "w-full flex items-center justify-center gap-2 rounded-lg py-2 px-3 text-sm font-semibold",
                "bg-white/15 text-white hover:bg-white/25 transition-colors border border-white/20",
                "disabled:cursor-not-allowed disabled:opacity-60"
              )}
            >
              <NewChatIcon className="w-4 h-4 shrink-0" />
              {SIDEBAR_NEW_CHAT.label}
            </button>
          </div>

          <nav className="flex-1 min-h-0 overflow-y-auto scrollbar-hide px-4 py-2 bg-black/10 backdrop-blur-sm md:bg-transparent space-y-2">
            {navSections.map((section) => {
              const sectionKey = `section:${section.id}`;
              const sectionOpen = isExpanded(sectionKey, section.defaultExpanded);
              const sectionActive = sectionContainsActive(location.pathname, location.state, section);

              return (
                <Collapsible
                  key={section.id}
                  open={sectionOpen}
                  onOpenChange={(next) => setExpanded(sectionKey, next)}
                >
                  <CollapsibleTrigger
                    className={cn(
                      "flex items-center w-full px-3 py-1.5 mb-0.5 rounded-lg transition-colors",
                      sectionActive ? "text-white" : "text-white/75 hover:text-white hover:bg-white/5"
                    )}
                  >
                    <span className="text-xs font-semibold uppercase tracking-wide flex-1 text-left">
                      {section.label}
                    </span>
                    <ChevronDown
                      size={14}
                      strokeWidth={3}
                      className={cn("transition-transform duration-200 shrink-0 opacity-80", sectionOpen && "rotate-180")}
                    />
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <ul className="space-y-0.5">{section.items.map((item) => renderNavItem(item))}</ul>
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
          </nav>

          <div className="p-3 bg-black/10 backdrop-blur-sm md:bg-transparent shrink-0 border-t border-white/10">
            <DropdownMenu open={userDropdownOpen} onOpenChange={setUserDropdownOpen}>
              <DropdownMenuTrigger className="w-full flex items-center gap-3 px-3 py-2 text-white/80 hover:text-white hover:bg-white/5 rounded-lg transition-colors outline-none">
                <div className="w-8 h-8 bg-bodhi-teal rounded-full flex items-center justify-center shrink-0 text-sm font-semibold text-white">
                  {(userData?.user_name?.trim()?.[0] ?? "U").toUpperCase()}
                </div>

                <span className="flex-1 text-left text-sm max-w-full font-medium text-ellipsis whitespace-nowrap overflow-hidden">
                  {userData?.user_name || "User"}
                </span>

                <div className="w-4 h-4 text-white/60">
                  <ChevronDown
                    size={14}
                    strokeWidth={3}
                    className={cn("transition-transform duration-200", userDropdownOpen && "rotate-180")}
                  />
                </div>
              </DropdownMenuTrigger>

              <DropdownMenuContent
                side="top"
                className="mb-2 bg-white/5 backdrop-blur-sm rounded-lg border border-white/20 overflow-hidden w-56"
              >
                <DropdownMenuItem
                  onClick={() => {
                    closeMobile();
                    navigate("/profile");
                  }}
                  className="w-full text-sm flex items-center p-3 text-white/80 hover:text-white focus:text-white hover:bg-white/10 focus:bg-white/5 transition-colors hover:cursor-pointer outline-none"
                >
                  <User size={14} strokeWidth={3} className="mr-3" />
                  Profile
                </DropdownMenuItem>

                <DropdownMenuItem
                  onClick={() => setIsLogout(true)}
                  className="w-full text-sm flex items-center p-3 text-white/80 hover:text-white focus:text-white hover:bg-white/10 focus:bg-white/5 transition-colors hover:cursor-pointer  outline-none"
                >
                  <LogOut size={14} strokeWidth={3} className="mr-3" />
                  Sign out
                </DropdownMenuItem>

                <DropdownMenuItem className="w-full text-sm flex items-center p-3 text-white/80 hover:text-white focus:text-white hover:bg-white/10 focus:bg-white/5 transition-colors hover:cursor-pointer outline-none">
                  <AtSign size={14} strokeWidth={3} className="mr-3" />
                  <a href="mailto:sam@aieducators.ai" className="w-full h-full">
                    Support
                  </a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <button
          type="button"
          onClick={() => setMobileMenuOpen(false)}
          className="md:hidden fixed top-3 right-3 z-[60] p-2 bg-white/10 rounded-lg text-white"
          aria-label="Close menu"
        >
          <X className="w-5 h-5" />
        </button>
      )}

      <AppModal
        isOpen={isLogout}
        onOpenChange={setIsLogout}
        onConfirm={signOut}
        title="Sign out"
        description="Are you sure you want to sign out?"
        confirmButtonText="Sign out"
      />
    </>
  );
};
